import * as React from "react";
import { cn } from "../utils";
import { Button } from "../button";
import { ProviderLogo } from "./marketing/provider-logo";

/**
 * SectionBlankState — the ONE consolidated blank state for an empty section.
 *
 * When a whole section has no data yet, its real first job is getting data IN.
 * This block states that calmly and low-profile (the Linear "empty view" idiom):
 * a thin line-art mark, a short title, a muted line or two, the smaller logos of
 * the apps that can fill it, and exactly two actions — a primary that brings the
 * data in (typically launching the Play onboarding walk for the section) and a
 * quiet "add by hand". Everything is CENTERED so it reads as the section's hero,
 * not a stray note.
 *
 * Presentational only: the host owns behavior. `onConnect` is the primary verb
 * (open Play, open a connect dialog, …); `onManual` is the optional secondary
 * (inline intake). Clicking a provider logo runs `onProviderSelect` if given,
 * else falls through to `onConnect`. Omit `onManual` for a section where a hand
 * path doesn't apply (e.g. web analytics) — the primary + logos stand alone.
 *
 * Prefer this over a bare `EmptyState` for a section cold-start, and over
 * `ColdStartChoices` (the doors/unlocks fork) — the design converged here:
 * low-profile, integration-forward, two actions, no payoff checklist.
 * → docs/design/BLANK-STATE-PATTERN.md
 */

export interface BlankStateProvider {
  /** Brand slug in BRAND_LOGO_FILES (e.g. "plausible", "hubspot", "stripe"). */
  slug: string;
  /** Display name — used for the logo's title/aria. */
  name: string;
}

export interface SectionBlankStateProps {
  /** Thin line-art mark (a stroke-1.25 lucide glyph). Rendered muted + large. */
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** The real apps that can fill this section — shown small + clickable. */
  providers?: BlankStateProvider[];
  /** Label above the logo row. Defaults to "Works with". */
  providersLabel?: string;
  /** Click handler for a specific provider logo (defaults to `onConnect`). */
  onProviderSelect?: (slug: string) => void;
  /** Primary action label. Defaults to "Connect a source". */
  connectLabel?: string;
  /** Primary action — the "bring data in" verb (launch Play, open a dialog…). */
  onConnect?: () => void;
  /** Secondary action label. Defaults to "Add manually". */
  manualLabel?: string;
  /** Secondary action. Omit to render only the primary. */
  onManual?: () => void;
  className?: string;
}

export function SectionBlankState({
  icon,
  title,
  description,
  providers,
  providersLabel = "Works with",
  onProviderSelect,
  connectLabel = "Connect a source",
  onConnect,
  manualLabel = "Add manually",
  onManual,
  className,
}: SectionBlankStateProps) {
  const selectProvider = (slug: string) => {
    if (onProviderSelect) return onProviderSelect(slug);
    onConnect?.();
  };

  return (
    <div
      data-slot="section-blank-state"
      className={cn(
        "mx-auto flex min-h-[360px] w-full max-w-xl flex-col items-center justify-center gap-6 px-6 py-12 text-center",
        className,
      )}
    >
      {icon ? (
        <div
          className="text-muted-foreground/70 [&_svg]:size-12 [&_svg]:stroke-[1.25]"
          aria-hidden
        >
          {icon}
        </div>
      ) : null}

      <div className="flex flex-col items-center gap-2.5">
        <h2 className="type-title font-medium text-foreground">{title}</h2>
        {description ? (
          <p className="max-w-md type-body text-muted-foreground">
            {description}
          </p>
        ) : null}
      </div>

      {providers && providers.length > 0 ? (
        <div className="flex flex-col items-center gap-2">
          <span className="type-subtext text-muted-foreground/60">{providersLabel}</span>
          <div className="flex flex-wrap items-center justify-center gap-1.5">
            {providers.map((p) => (
              <button
                key={p.slug}
                type="button"
                onClick={() => selectProvider(p.slug)}
                title={`Connect ${p.name}`}
                aria-label={`Connect ${p.name}`}
                className="rounded-xl opacity-80 outline-none transition-opacity duration-150 hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <ProviderLogo provider={p.slug} size="sm" />
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {onConnect || onManual ? (
        <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
          {onConnect ? <Button onClick={onConnect}>{connectLabel}</Button> : null}
          {onManual ? (
            <Button variant="outline" onClick={onManual}>
              {manualLabel}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
